import React, { useState } from "react";
import { gql } from "@apollo/client";
import {
  HtmlTable,
  HtmlTableBody,
  HtmlTableCell,
  HtmlTableHead,
  HtmlTableHeader,
  HtmlTableRow,
} from "@src/lib/ui/HtmlTable";
import { Skeleton } from "@src/lib/ui/Skeleton";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@src/lib/ui/AlertDialog";
import {
  useOrgGuestFeedbackCsvUploadsQuery,
  useDeleteOrgGuestFeedbackCsvUploadMutation,
  OrgGuestFeedbackCsvUploadFragment,
} from "./GuestFeedbackCsvUploadsTab.generated";
import { useDetailSidebarContext } from "@src/lib/contexts/DetailSidebarContext";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import DotsIcon from "@src/lib/icons/18px/dots";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import { useToast } from "@src/lib/hooks/useToast";
import { useGuestFeedbackCsvImport } from "./useGuestFeedbackCsvImport";
import { csvUploadFileName } from "./csvUploadFileName";
import { GraphqlOperations } from "@src/types.generated";

gql`
  fragment OrgGuestFeedbackCsvUpload on OrgGuestFeedbackCsvUpload {
    id
    fileUrl
    fileName
    feedbackDateColumn
    feedbackTextColumn
    ratingColumn
    locationColumn
    feedbackSource
    feedbacks {
      id
    }
    locations {
      id
      locationName
      location {
        id
        name
      }
    }
  }

  query OrgGuestFeedbackCsvUploads {
    OrgGuestFeedbackCsvUploads {
      objects {
        ...OrgGuestFeedbackCsvUpload
      }
      totalCount
    }
  }

  mutation DeleteOrgGuestFeedbackCsvUpload($id: UUID!) {
    deleteOrgGuestFeedbackCsvUpload(id: $id) {
      success
      error {
        message
      }
    }
  }
`;

const GuestFeedbackCsvUploadsTab: React.FC = () => {
  const { addToast, addErrorToast } = useToast();
  const { _toggleSidebar } = useDetailSidebarContext();
  const { notifyCsvDeleted } = useGuestFeedbackCsvImport();
  const { data, loading } = useOrgGuestFeedbackCsvUploadsQuery();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteUpload, setPendingDeleteUpload] =
    useState<OrgGuestFeedbackCsvUploadFragment | null>(null);

  const [deleteUpload] = useDeleteOrgGuestFeedbackCsvUploadMutation({
    refetchQueries: [
      GraphqlOperations.Query.OrgGuestFeedbackCsvUploads,
      GraphqlOperations.Query.GuestFeedbackSetupStatus,
    ],
    awaitRefetchQueries: true,
    onCompleted: (result) => {
      if (result.deleteOrgGuestFeedbackCsvUpload.success) {
        notifyCsvDeleted();
        addToast({ message: "CSV upload deleted" });
      } else {
        addErrorToast({
          message:
            result.deleteOrgGuestFeedbackCsvUpload.error?.message ??
            "Failed to delete CSV upload",
          callsite: "guest_feedback_csv_upload_delete",
        });
      }
    },
  });

  const uploads = data?.OrgGuestFeedbackCsvUploads?.objects ?? [];

  const handleDelete = (
    e: React.MouseEvent,
    upload: OrgGuestFeedbackCsvUploadFragment,
  ) => {
    e.stopPropagation();
    setPendingDeleteUpload(upload);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!pendingDeleteUpload) return;
    deleteUpload({
      variables: {
        id: pendingDeleteUpload.id,
      },
    });
    setDeleteDialogOpen(false);
    setPendingDeleteUpload(null);
  };

  if (loading && !data) {
    return (
      <div className="flex flex-col gap-2 py-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  if (uploads.length === 0) {
    return (
      <EmptyState className="flex-none px-4 py-8">
        <EmptyStateTitle>No CSV uploads</EmptyStateTitle>
        <EmptyStateDescription>
          CSV uploads will appear here once you upload guest feedback data via
          CSV.
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 py-4">
        <Text
          variant="fine-print-description"
          className="text-muted-foreground"
        >
          {uploads.length} upload{uploads.length !== 1 ? "s" : ""}
        </Text>

        <HtmlTable className="w-full">
          <HtmlTableHeader>
            <HtmlTableRow>
              <HtmlTableHead className="w-[250px]">File</HtmlTableHead>
              <HtmlTableHead className="w-[160px]">Source</HtmlTableHead>
              <HtmlTableHead className="w-[100px]">Locations</HtmlTableHead>
              <HtmlTableHead className="w-[100px]">Reviews</HtmlTableHead>
              <HtmlTableHead className="w-[50px]" />
            </HtmlTableRow>
          </HtmlTableHeader>
          <HtmlTableBody>
            {uploads.map((upload) => (
              <HtmlTableRow
                key={upload.id}
                className="cursor-pointer"
                onClick={(e) =>
                  _toggleSidebar(
                    e,
                    upload.id,
                    DetailSidebarType.GuestFeedbackCsvUpload,
                    "guest-feedback-csv-uploads",
                  )
                }
              >
                <HtmlTableCell>
                  <Text variant="compact-title" className="truncate">
                    {csvUploadFileName(upload)}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text
                    variant="compact-description"
                    className="truncate text-muted-foreground"
                  >
                    {upload.feedbackSource
                      ? `${upload.feedbackSource} (CSV)`
                      : "—"}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text
                    variant="compact-description"
                    className="text-muted-foreground"
                  >
                    {upload.locations.length}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <Text
                    variant="compact-description"
                    className="text-muted-foreground"
                  >
                    {upload.feedbacks.length}
                  </Text>
                </HtmlTableCell>
                <HtmlTableCell>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger
                      asChild
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button variant="ghost" size="icon">
                        <DotsIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" side="bottom">
                      <DropdownMenuItem
                        className="cursor-pointer text-destructive"
                        onClick={(e) => handleDelete(e, upload)}
                      >
                        <TrashOutlineIcon />
                        <Text variant="compact-title">Delete</Text>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </HtmlTableCell>
              </HtmlTableRow>
            ))}
          </HtmlTableBody>
        </HtmlTable>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete CSV Upload</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this CSV upload? This will also
              delete all reviews that were imported from this file. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Cancel</Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GuestFeedbackCsvUploadsTab;
